import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import ScrollReveal from "@/components/ScrollReveal";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FORMSUBMIT_URL = "https://formsubmit.co/ajax/hola@cree8solutions.org";
const BACKEND_API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

export default function Contact() {
  const { t } = useLanguage();
  const c = t.contactPage;
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.service || !form.budget || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      // Store in backend DB as backup
      if (BACKEND_API) {
        fetch(`${BACKEND_API}/contact`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        }).catch(() => {});
      }

      // Send via FormSubmit.co for email delivery
      const res = await fetch(FORMSUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          _replyto: form.email,
          company: form.company || "Not provided",
          service: form.service || "Not specified",
          budget: form.budget || "Not specified",
          message: form.message,
          _subject: `New Inquiry from ${form.name} — CREE8 Solutions`,
          _template: "table",
          _captcha: "false",
        }),
      });
      const data = await res.json();

      if (data.success === "true" || data.success === true) {
        toast.success(c.form.successTitle, { description: c.form.successText });
        setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
      } else if (data.message && data.message.toLowerCase().includes("activat")) {
        toast.success(c.form.successTitle, {
          description: "Please check hola@cree8solutions.org to activate email delivery.",
          duration: 8000,
        });
        setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
      } else {
        // Still stored in backend, show success with note
        toast.success(c.form.successTitle, { description: c.form.successText });
        setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
      }
    } catch {
      // If FormSubmit fails but backend stored it, still show success
      if (BACKEND_API) {
        toast.success(c.form.successTitle, { description: c.form.successText });
        setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
      } else {
        toast.error(c.form.errorText);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-16 sm:pb-20 bg-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {c.hero.overline}
            </p>
            <h1 data-testid="contact-page-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl">
              {c.hero.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl">
              {c.hero.description}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-16 sm:py-24 bg-[#333333]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <ScrollReveal>
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-3">
                        {c.form.name} *
                      </label>
                      <input
                        type="text"
                        data-testid="contact-name"
                        value={form.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        placeholder={c.form.namePlaceholder}
                        className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-[#E8A400] focus:outline-none transition-colors placeholder:text-white/30 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-3">
                        {c.form.email} *
                      </label>
                      <input
                        type="email"
                        data-testid="contact-email"
                        value={form.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        placeholder={c.form.emailPlaceholder}
                        className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-[#E8A400] focus:outline-none transition-colors placeholder:text-white/30 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-3">
                      {c.form.company} *
                    </label>
                    <input
                      type="text"
                      data-testid="contact-company"
                      value={form.company}
                      onChange={(e) => updateField("company", e.target.value)}
                      placeholder={c.form.companyPlaceholder}
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-[#E8A400] focus:outline-none transition-colors placeholder:text-white/30 text-sm"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-3">
                        {c.form.service} *
                      </label>
                      <Select
                        value={form.service}
                        onValueChange={(val) => updateField("service", val)}
                        required
                      >
                        <SelectTrigger
                          data-testid="contact-service-select"
                          className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-1 py-3 text-sm text-white focus:ring-0 focus:border-[#E8A400] h-11 shadow-none [&>svg]:text-white/50"
                        >
                          <SelectValue placeholder={c.form.servicePlaceholder} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2A2A2A] border-white/10 z-[100]">
                          {c.form.serviceOptions.map((opt) => (
                            <SelectItem
                              key={opt}
                              value={opt}
                              className="text-white/80 focus:bg-white/10 focus:text-white cursor-pointer"
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-3">
                        {c.form.budget} *
                      </label>
                      <Select
                        value={form.budget}
                        onValueChange={(val) => updateField("budget", val)}
                        required
                      >
                        <SelectTrigger
                          data-testid="contact-budget-select"
                          className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-1 py-3 text-sm text-white focus:ring-0 focus:border-[#E8A400] h-11 shadow-none [&>svg]:text-white/50"
                        >
                          <SelectValue placeholder={c.form.budgetPlaceholder} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2A2A2A] border-white/10 z-[100]">
                          {c.form.budgetOptions.map((opt) => (
                            <SelectItem
                              key={opt}
                              value={opt}
                              className="text-white/80 focus:bg-white/10 focus:text-white cursor-pointer"
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-3">
                      {c.form.message} *
                    </label>
                    <textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => updateField("message", e.target.value)}
                      placeholder={c.form.messagePlaceholder}
                      rows={5}
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-[#E8A400] focus:outline-none transition-colors placeholder:text-white/30 text-sm resize-none"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={sending}
                    className="bg-[#E8A400] text-[#1A1A1A] font-semibold px-10 py-4 hover:bg-[#C68C00] transition-colors duration-300 rounded-sm text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? c.form.sending : c.form.submit}
                  </button>
                </form>
              </ScrollReveal>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5">
              <ScrollReveal delay={200}>
                <div className="bg-[#2A2A2A] border border-white/10 p-8 lg:p-10 sticky top-28">
                  <h3 className="text-xl font-semibold tracking-tight mb-8">
                    {c.info.title}
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <Mail className="w-5 h-5 text-[#E8A400] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-1">
                          {c.info.emailLabel}
                        </p>
                        <a
                          href={`mailto:${c.info.email}`}
                          data-testid="contact-info-email"
                          className="text-white hover:text-[#E8A400] transition-colors text-sm"
                        >
                          {c.info.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Phone className="w-5 h-5 text-[#E8A400] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-1">
                          {c.info.phoneLabel}
                        </p>
                        <a
                          href={`tel:${c.info.phone.replace(/\s/g, "")}`}
                          data-testid="contact-info-phone"
                          className="text-white hover:text-[#E8A400] transition-colors text-sm"
                        >
                          {c.info.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <MapPin className="w-5 h-5 text-[#E8A400] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-white/40 mb-1">
                          {c.info.addressLabel}
                        </p>
                        <div data-testid="contact-info-address" className="text-sm text-white/70">
                          {c.info.address.map((line, i) => (
                            <span key={i} className="block">{line}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
